import type { ReactNode } from 'react';
import { Icon } from '@leanin/ui';
import { ExternalLink } from '@/components/external-link';
import styles from './SocialIconLink.module.css';

/**
 * Single circular social-icon link. 40px hit-target with the warm-grey DS
 * fill (`--muted`) and a mid-grey glyph (`--muted-foreground`), opening in a
 * new tab.
 *
 * The href is member- or leader-entered free text (a member's own profile
 * links, or a circle/network leader's group links), so cross-origin http(s)
 * destinations route through the `ExternalLink` interstitial (design
 * 2026-08-06 Project B) — the same phishing mitigation as post/comment/DM
 * links, applied to the other surface where a person types a URL others click.
 * `mailto:` group links fall straight through to the mail client. The label is
 * read out by screen readers and forms the aria-label.
 *
 * Used by the profile chrome's About facts card and by group surfaces
 * (`GroupSocialLinksCard` for circles + networks). Same render → same
 * appearance across all three.
 */
export function SocialIconLink({
  href,
  label,
  glyph,
}: {
  readonly href: string;
  readonly label: string;
  readonly glyph: ReactNode;
}) {
  return (
    <ExternalLink
      href={href}
      aria-label={`${label} (opens in new tab)`}
      className={styles.socialIconLink}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: 'var(--radius-full)',
        background: 'var(--muted)',
        color: 'var(--muted-foreground)',
        flexShrink: 0,
      }}
    >
      <Icon size="md" color="var(--muted-foreground)">{glyph}</Icon>
    </ExternalLink>
  );
}
